import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  History, Search, RefreshCw, Filter, Trash2, Eye, Download,
  CheckCircle, XCircle, Clock, AlertTriangle
} from 'lucide-react';
import { scansAPI, tenantsAPI, downloadExport, providerIcons } from '../services/amidala';

const AmidalaScans = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.startsWith('/amidala') ? '/amidala' : '/grc/amidala';
  const [loading, setLoading] = useState(true);
  const [tenant, setTenant] = useState(null);
  const [scans, setScans] = useState([]);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    loadData();
  }, [statusFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const tenantsRes = await tenantsAPI.list();
      if (tenantsRes.data.length === 0) {
        navigate(basePath);
        return;
      }
      
      const currentTenant = tenantsRes.data[0];
      setTenant(currentTenant);
      
      const params = statusFilter ? { status: statusFilter } : {};
      const scansRes = await scansAPI.list(currentTenant.id, { ...params, limit: 50 });
      setScans(scansRes.data);
    } catch (err) {
      setError('Failed to load scans');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteScan = async (scanId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this scan and all its findings?')) return;
    
    try {
      await scansAPI.delete(scanId);
      loadData();
    } catch (err) {
      setError('Failed to delete scan');
    }
  };

  const handleExport = async (scanId, format, e) => {
    e.stopPropagation();
    try {
      await downloadExport(scanId, format, `cspm_scan_${scanId}.${format}`);
    } catch (err) {
      setError('Export failed');
    }
  };

  const filteredScans = scans.filter(scan => {
    if (!filter) return true;
    return scan.name.toLowerCase().includes(filter.toLowerCase());
  });

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'running': return <RefreshCw className="w-5 h-5 text-blue-600 animate-spin" />;
      case 'failed': return <XCircle className="w-5 h-5 text-red-600" />;
      case 'pending': return <Clock className="w-5 h-5 text-gray-600" />;
      default: return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <History className="w-8 h-8 text-indigo-600" />
            Scan History
          </h1>
          <p className="text-gray-600 mt-1">View and manage your security scans</p>
        </div>
        
        <button
          onClick={() => navigate(`${basePath}/scan`)}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
        >
          <Search className="w-4 h-4" />
          New Scan
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl shadow p-4 flex items-center gap-4">
        <Filter className="w-5 h-5 text-gray-400" />
        
        <div className="flex-1 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search scans..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
        </div>
        
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
        >
          <option value="">All Statuses</option>
          <option value="completed">Completed</option>
          <option value="running">Running</option>
          <option value="pending">Pending</option>
          <option value="failed">Failed</option>
        </select>
        
        <button
          onClick={loadData}
          className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
          title="Refresh"
        >
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      {/* Scans List */}
      {filteredScans.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-12 text-center">
          <History className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No Scans Found</h2>
          <p className="text-gray-600 mb-6">
            {scans.length === 0 
              ? "You haven't run any security scans yet."
              : "No scans match your search criteria."}
          </p>
          <button
            onClick={() => navigate(`${basePath}/scan`)}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Run Your First Scan
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">
                  Scan
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase">
                  Accounts
                </th>
                <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase">
                  Findings
                </th>
                <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase">
                  Compliance
                </th>
                <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredScans.map((scan) => {
                const summary = scan.summary || {};
                const findings = (summary.critical_findings || 0) + (summary.high_findings || 0) + 
                               (summary.medium_findings || 0) + (summary.low_findings || 0);
                
                return (
                  <tr 
                    key={scan.id}
                    onClick={() => navigate(`${basePath}/scans/${scan.id}`)}
                    className="hover:bg-gray-50 cursor-pointer"
                  >
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-gray-900">{scan.name}</p>
                        <p className="text-sm text-gray-500">
                          {new Date(scan.created_at).toLocaleString()}
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(scan.status)}
                        <span className={`text-sm font-medium ${
                          scan.status === 'completed' ? 'text-green-600' :
                          scan.status === 'running' ? 'text-blue-600' :
                          scan.status === 'failed' ? 'text-red-600' :
                          'text-gray-600'
                        }`}>
                          {scan.status}
                        </span>
                      </div>
                      {scan.duration_seconds && (
                        <p className="text-xs text-gray-400 mt-1">
                          {scan.duration_seconds}s
                        </p>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-600">
                        {scan.accounts_scanned?.length || 0} accounts
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      {scan.status === 'completed' ? (
                        <div className="flex items-center justify-center gap-2">
                          {summary.critical_findings > 0 && (
                            <span className="px-2 py-0.5 bg-red-100 text-red-800 rounded text-xs font-medium">
                              {summary.critical_findings} C
                            </span>
                          )}
                          {summary.high_findings > 0 && (
                            <span className="px-2 py-0.5 bg-orange-100 text-orange-800 rounded text-xs font-medium">
                              {summary.high_findings} H
                            </span>
                          )}
                          {summary.medium_findings > 0 && (
                            <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 rounded text-xs font-medium">
                              {summary.medium_findings} M
                            </span>
                          )}
                          {findings === 0 && (
                            <span className="text-green-600 text-sm">No issues</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {scan.status === 'completed' && summary.compliance_score != null ? (
                        <span className={`font-medium ${
                          summary.compliance_score >= 80 ? 'text-green-600' :
                          summary.compliance_score >= 60 ? 'text-yellow-600' : 'text-red-600'
                        }`}>
                          {summary.compliance_score.toFixed(1)}%
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`${basePath}/scans/${scan.id}`);
                          }}
                          className="p-2 text-gray-600 hover:bg-gray-100 rounded"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        
                        {scan.status === 'completed' && (
                          <button
                            onClick={(e) => handleExport(scan.id, 'json', e)}
                            className="p-2 text-gray-600 hover:bg-gray-100 rounded"
                            title="Export JSON"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        )}
                        
                        <button
                          onClick={(e) => handleDeleteScan(scan.id, e)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded"
                          title="Delete Scan"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AmidalaScans;
